import {
  CreateExtraAchievementDTO,
  ExtraAchievementDTO,
  TargetUserDTO,
} from "@/modules/agents/dtos/index.v2";
import { useCreateExtraAchievement } from "@/modules/agents/hooks/create-extra-achievment.hook";
import {
  useDeleteExtraAchievement,
  useExtraAchievments,
} from "@/modules/agents/hooks/extra-achievments.hook";
import MoneyInput from "@/modules/common/components/input/money-input";
import ResponsiveEditDialog from "@/modules/common/components/modals/responsive-edit-dialog";
import PermissionGuard from "@/modules/common/components/wrappers/permission-guard";
import { NawyPassportContext } from "@/modules/common/context/nawy-passport.provider";
import {
  PermissionLevel,
  PermissionsEnum,
  TOAST_IDS,
  ToastType,
} from "@/modules/common/types";
import { showToast, userHasPermission } from "@/modules/common/utils";
import { getQuarterInfo, getQuarterStartDate } from "@/modules/targets/utils";
import { muiTheme } from "@/styles/theme";
import {
  Box,
  Button,
  CircularProgress,
  DialogContent,
  Divider,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { endOfMonth, startOfMonth } from "date-fns";
import { Eye, Pencil, Plus, Trash2 } from "lucide-react";
import { useContext, useMemo, useState } from "react";

const ExtraAchievementRow = ({
  data,
  disabled,
  year,
  id,
  monthRange,
  handleRemoveRow,
  handleUpdateRow,
}: {
  data: Omit<ExtraAchievementDTO, "user" | "id">;
  disabled?: boolean;
  year: number;
  userUId: string;
  id: number;
  monthRange: number[];
  handleRemoveRow?: (index: number) => void;
  handleUpdateRow?: (
    index: number,
    data: {
      amount?: number;
      monthIndex?: number;
    }
  ) => void;
}) => {
  const deleteMutation = useDeleteExtraAchievement();

  const { amount: extraAchieved, from } = data ?? {};

  const monthIndex = useMemo(() => {
    return new Date(from).getMonth();
  }, [from]);

  return (
    <Box sx={{ display: "flex", alignItems: "center", pb: "16px" }}>
      <MoneyInput
        value={extraAchieved}
        label="Value"
        disabled={disabled}
        onChange={(e) => {
          handleUpdateRow?.(id, {
            amount: +e.target.value,
          });
        }}
      />
      <Select
        sx={{ mx: "24px" }}
        value={monthIndex}
        disabled={disabled}
        onChange={(e) => {
          handleUpdateRow?.(id, {
            monthIndex: +e.target.value,
          });
        }}
        fullWidth
      >
        {monthRange.map((month) => (
          <MenuItem key={month} value={month}>
            {new Date(year, month).toLocaleString("default", {
              month: "long",
            })}
          </MenuItem>
        ))}
      </Select>
      <PermissionGuard
        permissions={[
          {
            name: PermissionsEnum.EXTRA_ACHIEVEMENTS,
            level: PermissionLevel.WRITE,
          },
        ]}
      >
        <IconButton
          onClick={() => {
            if (handleRemoveRow) {
              handleRemoveRow?.(monthIndex);
            } else {
              deleteMutation.mutate({
                id: id,
              });
            }
          }}
          color="error"
        >
          {deleteMutation.isLoading ? (
            <CircularProgress color="error" size={20} />
          ) : (
            <Trash2 />
          )}
        </IconButton>
      </PermissionGuard>
    </Box>
  );
};

const monthsRanges = {
  1: [0, 1, 2],
  2: [3, 4, 5],
  3: [6, 7, 8],
  4: [9, 10, 11],
};

const UpdateExtraAchievements = ({
  user,
  quarter,
  year,
}: {
  quarter: 1 | 2 | 3 | 4;
  user: TargetUserDTO;
  year: number;
}) => {
  const { permissions: currentUserPermissions } =
    useContext(NawyPassportContext);

  const hasPermission = userHasPermission(
    {
      name: PermissionsEnum.EXTRA_ACHIEVEMENTS,
      level: PermissionLevel.WRITE,
    },
    currentUserPermissions
  );

  const monthRange = monthsRanges[quarter];

  const quarterInfo = getQuarterInfo(getQuarterStartDate(year, quarter));
  const { data: existingExtraAchievementsResponse } = useExtraAchievments({
    query: {
      userUId: user.cognitoId,
      from: quarterInfo.startDate.toISOString(),
      to: quarterInfo.endDate.toISOString(),
    },
  });

  const { mutateAsync, isLoading } = useCreateExtraAchievement();

  const [newExtraAchievements, setNewExtraAchievements] = useState<
    CreateExtraAchievementDTO[]
  >([]);

  const handleAddRow = () => {
    const startDate = startOfMonth(new Date(year, monthRange[0]));
    const endDate = endOfMonth(new Date(year, monthRange[0]));

    setNewExtraAchievements((prev) => [
      ...prev,
      {
        from: startDate.toISOString(),
        to: endDate.toISOString().replace(".999", ".000"),
        amount: 0,
        userUId: user.cognitoId,
      },
    ]);
  };

  const handleRemoveRow = (index: number) => {
    const updatedExtraAchievements = newExtraAchievements.filter(
      (_, i) => i !== index
    );

    setNewExtraAchievements(updatedExtraAchievements);
  };

  const handleUpdateRowValue = (
    index: number,
    {
      amount,
      monthIndex,
    }: {
      amount?: number;
      monthIndex?: number;
    }
  ) => {
    const updatedExtraAchievements = newExtraAchievements.map((extra, i) => {
      if (i === index) {
        const updatedExtra = { ...extra };

        if (amount) {
          updatedExtra.amount = amount;
        }

        if (monthIndex) {
          updatedExtra.from = startOfMonth(
            new Date(year, monthIndex)
          ).toDateString();
          updatedExtra.to = endOfMonth(
            new Date(year, monthIndex)
          ).toDateString();
        }
        return updatedExtra;
      }

      return extra;
    });

    setNewExtraAchievements(updatedExtraAchievements);
  };

  const handleSaveAndClose = async () => {
    const newExtraAchievementsPromises = newExtraAchievements.map((extra) => {
      return mutateAsync({
        payload: extra,
      });
    });

    await Promise.all(newExtraAchievementsPromises)
      .then(() => {
        setNewExtraAchievements([]);
        handleClose();
        showToast({
          type: ToastType.SUCCESS,
          message: "Extra achievement created successfully",
          toastId: TOAST_IDS.CREATE_EXTRA_ACHIEVEMENT_SUCCESS,
          options: {
            position: "bottom-left",
            autoClose: 3000,
          },
        });
      })
      .catch(() => {
        showToast({
          type: ToastType.ERROR,
          message: "An error occurred",
          toastId: TOAST_IDS.CREATE_EXTRA_ACHIEVEMENT_ERROR,
          options: {
            position: "bottom-left",
            autoClose: 3000,
          },
        });
      });
  };

  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const renderContent = () => {
    return (
      <ResponsiveEditDialog
        open={open}
        setOpen={setOpen}
        handleConfirm={handleSaveAndClose}
        handleCloseModal={handleClose}
        confirmText="Save"
        cancelText="Discard"
        modalFooter={hasPermission}
        hideModalFooter={!hasPermission}
        header={
          hasPermission
            ? "Update Personal Extra Achievements"
            : "Extra Achievements"
        }
        disabledConfirmButton={
          newExtraAchievements.some(
            (extraAchievement) => !extraAchievement.amount
          ) ||
          isLoading ||
          newExtraAchievements.length === 0
        }
        isLoading={isLoading}
      >
        <>
          <DialogContent sx={{ p: "24px 0 0 0" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="subtitle1">Value (EGP)</Typography>
              <Typography variant="subtitle1" sx={{ pr: 43.5 }}>
                Month
              </Typography>
            </Box>
            <Box sx={{ py: "16px" }}>
              <Divider></Divider>
            </Box>

            {existingExtraAchievementsResponse?.data?.map((target, index) => (
              <ExtraAchievementRow
                key={index}
                id={target.id}
                userUId={user.cognitoId}
                data={target}
                disabled={true}
                monthRange={monthRange}
                year={year}
              />
            ))}

            {newExtraAchievements.map((extraAchievement, index) => (
              <ExtraAchievementRow
                key={index}
                data={extraAchievement}
                userUId={user.cognitoId}
                id={index}
                disabled={!hasPermission}
                monthRange={monthRange}
                year={year}
                handleRemoveRow={handleRemoveRow}
                handleUpdateRow={handleUpdateRowValue}
              />
            ))}

            <PermissionGuard
              permissions={[
                {
                  name: PermissionsEnum.EXTRA_ACHIEVEMENTS,
                  level: PermissionLevel.WRITE,
                },
              ]}
            >
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  startIcon={<Plus />}
                  onClick={handleAddRow}
                  variant="outlined"
                  fullWidth
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100px",
                    m: "0 0 16px",
                    color: muiTheme.colorSchemes.light.palette.main.black,
                    backgroundColor:
                      muiTheme.colorSchemes.light.palette.background.white,
                    border: "1px solid  #D0D5DD",
                    ...muiTheme.typography.h4_med,
                  }}
                >
                  New
                </Button>
              </Box>
            </PermissionGuard>
          </DialogContent>
          <Box sx={{ p: "0 0 24px" }}>
            <Divider></Divider>
          </Box>
        </>
      </ResponsiveEditDialog>
    );
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          padding: "10px 16px",
          alignItems: "center",
          width: "100%",
          height: "100%",
          cursor: "pointer",
          ":hover": {
            backgroundColor:
              muiTheme.colorSchemes.light.palette.main.actionDisabled,
          },
        }}
        onClick={handleOpen}
      >
        {hasPermission ? (
          <Pencil
            style={{
              color: muiTheme.colorSchemes.light.palette.primary[500],
              width: "20px",
              height: "20px",
            }}
          />
        ) : (
          <Eye
            style={{
              color: muiTheme.colorSchemes.light.palette.primary[500],
              width: "20px",
              height: "20px",
            }}
          />
        )}
        <Typography
          sx={{
            color: "#015C9A",
          }}
          variant="h5_bold"
        >
          {hasPermission
            ? "Update Personal Extra Achievements"
            : "View Personal Extra Achievements"}
        </Typography>
      </Box>
      {renderContent()}
    </>
  );
};

export default UpdateExtraAchievements;
